import { Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, LinearProgress, List, ListItemIcon, ListItemText, Menu, MenuItem, Radio, RadioGroup as MuiRadioGroup, Switch, TextField, Typography } from "@mui/material";
import { AddShoppingCartOutlined, BlockOutlined, CallMerge, CheckCircleOutline, Close as CloseIcon, ConfirmationNumberOutlined, Inventory2Outlined, Savings, WarningAmberRounded } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from "react";
import { Etiqueta, ImprimirEtiquetas } from '../../../components/etiquetas';
import { STATUS_SALE } from '../../../helpers/enums';
import { ccyFormat } from "../../../helpers/helper";
import PrintService from '../../../services/imprimir';
import SaleData from "./SaleData";
import CashierSaleGrid from './components/CashierSaleGrid';
import RadioGroup from '../../../components/RadioGroup';
import RaffleService from '../../../services/raffle';
import './styles.css';

const normalizeText = (value = '') => value
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const toLocalDateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const directMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (directMatch?.[1]) return directMatch[1];
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isReservedSale = (sale) => {
  const normalized = normalizeText(sale?.status || '');
  return normalized === normalizeText(STATUS_SALE.RESERVED) || normalized === 'reserved';
};

const isSameLocalDate = (value) => {
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return toLocalDateKey(value) === todayKey;
};

const Sales = ({ loading, salesData = [], onCompleteSales, onDeleteItem, onReserveItem, onReturnItem, onMergePendingSales, onAddDeposit, onAddItems, onDeliverGroup, onCancelGroup, onAssignRaffle, raffleMinimo = 0, onUpdateHeld, readonly = false, showImprimir = false, groupByDate = false, users, tittle = '', showTotals = false, asList = false, visualVariant = 'default', showPaidWatermark = false }) => {
  const refAImprimir = useRef();
  const [toPrint, setToPrint] = useState([]);
  const [selectedUser, setSelectedUser] = React.useState("");
  const [onlyTodayPending, setOnlyTodayPending] = useState(false);
  const u = [...(new Set(salesData?.map(item => item.userId)))]
  const userInSales = users?.filter(user => u.includes(user._id)).concat({ name: 'TODOS', _id: 'all' })

  const groupSales = (sales) => {
    const result = {};

    sales?.forEach(item => {
      const { nameSale } = item;

      // Si la categoría nameSale aún no existe en el resultado, la inicializamos con un objeto
      if (!result[nameSale]) {
        result[nameSale] = { checked: false, sales: [] };
      }

      // Agregar el item actual al array de sales correspondiente a su categoría nameSale
      result[nameSale].sales.push(item);
    });

    // Crear la estructura final agrupando por status (y origen web separado)
    const finalResult = {};

    Object.keys(result).forEach(nameSale => {
      result[nameSale].sales.forEach(item => {
        const { status } = item;
        const originSuffix = item?.origin === 'transfer_order' ? '_web' : '';

        // Las órdenes web tienen su propio grupo aunque compartan nameSale y status
        if (!finalResult[`${nameSale}_${status}${originSuffix}`]) {
          finalResult[`${nameSale}_${status}${originSuffix}`] = { checked: false, sales: [] };
        }

        finalResult[`${nameSale}_${status}${originSuffix}`].sales.push(item);
      });
    });

    const sortedFinalResult = Object.keys(finalResult).sort().reduce((acc, key) => {
      acc[key] = finalResult[key];
      return acc;
    }, {});

    return sortedFinalResult;
  };

  const [sales, setSales] = useState(groupSales(salesData));
  const [totalSelected, setTotalSelected] = useState(0);
  const [mergeDialog, setMergeDialog] = useState({ open: false, sourceKey: '', targetKey: '' });
  const [selectedMergeName, setSelectedMergeName] = useState('');
  const [mergingSales, setMergingSales] = useState(false);
  const [mergeMenuAnchor, setMergeMenuAnchor] = useState(null);
  const [mergeMenuSourceKey, setMergeMenuSourceKey] = useState('');
  const [depositDialog, setDepositDialog] = useState({ open: false, sourceKey: '' });
  const [addingDeposit, setAddingDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);
  const [deliverDialog, setDeliverDialog] = useState({ open: false, sourceKey: '' });
  const [selectedDeliverIds, setSelectedDeliverIds] = useState([]);
  const [deliveringSales, setDeliveringSales] = useState(false);
  const [cancelGroupDialog, setCancelGroupDialog] = useState({ open: false, sourceKey: '' });
  const [cancellingGroup, setCancellingGroup] = useState(false);
  const [raffleDialog, setRaffleDialog] = useState({ open: false, sourceKey: '' });
  const [raffleForm, setRaffleForm] = useState({ numero_rifa: '', lote_rifa: '', dni: '', telefono: '' });
  const [assigningRaffle, setAssigningRaffle] = useState(false);
  const [raffleLoadingNext, setRaffleLoadingNext] = useState(false);
  const [raffleError, setRaffleError] = useState('');
  const [deliverHeldMap, setDeliverHeldMap] = useState({});
  const canMergeByAction = Boolean(onMergePendingSales);
  const canAddDepositByAction = Boolean(onAddDeposit);
  const canAddItemsByAction = Boolean(onAddItems);
  const canDeliverByAction = Boolean(onDeliverGroup);
  const canCancelGroupByAction = Boolean(onCancelGroup);
  const canAssignRaffleByAction = Boolean(onAssignRaffle);

  const auxSum = Object.keys(sales).reduce((acc, key) => {
    const sal = sales[key]?.sales;
    if (sal && Array.isArray(sal)) {
      let totalPrice = sal.reduce((sum, sale) => {
        var efectivo = sale.payments?.find(p => p.name === "Efectivo")
        var transf = sale.payments?.find(p => p.name === "Transferencia")

        if ([STATUS_SALE.RESERVED, STATUS_SALE.FINISHED].includes(sale.status)) {
          sum.totalPrices += sale.quantity_sold * sale?.prices_sold.sale_price - sale.deposit
          // sum.totalCash += efectivo && sale.deposit > 0 && efectivo.amount > sale.deposit ? efectivo?.amount - sale.deposit : efectivo ? efectivo.amount : 0 
          // sum.totalTransf += transf && sale.deposit > 0 && transf.amount > sale.deposit ? transf?.amount - sale.deposit : transf ? transf.amount : 0
          sum.totalCash += efectivo ? efectivo.amount : 0
          sum.totalTransf += transf ? transf.amount : 0
        }
        return sum
      }, {totalPrices: 0, totalCash: 0, totalTransf: 0});

      // let totalPrices = sal.reduce((sum, sale) => sum + ([STATUS_SALE.RESERVED, STATUS_SALE.FINISHED].includes(sale.status) ? sale.quantity_sold * sale?.prices_sold.sale_price : 0), 0);

      let totalQuantity = sal.reduce((sum, sale) =>
        sum + ([STATUS_SALE.RESERVED, STATUS_SALE.FINISHED].includes(sale.status) ? sale.quantity_sold : 0), 0
      );

      acc[key] = {
        // totalPrices,
        totalQuantity,
        totalPrice,
      }
    } else {
      //acc[key] = 0; // Si no hay orders, sumar 0
      acc[key] = { totalPrices: 0, totalQuantity: 0, totalPrice: {totalPrices: 0, totalCash: 0, totalTransf: 0} };
    }
    return acc;
  }, {});

  //const totalSum = Object.values(auxSum).reduce((sum, quantity) => sum + quantity, 0);

  // const totalSum = Object.values(auxSum).reduce((sum, item) => sum + item.totalPrices, 0);
  const values = Object.values(auxSum)

  const totalQuant = values.reduce((sum, item) => sum + item.totalQuantity, 0);

  const totalSum2 = values.reduce((sum, item) => sum + item.totalPrice.totalPrices, 0);

  const totalQuant2 = values.reduce((sum, item) => sum + item.totalPrice.totalCash, 0);
  const totalQuant3 = values.reduce((sum, item) => sum + item.totalPrice.totalTransf, 0);
  // const totalSum = 0;

  const showButton = Object.values(sales).some(sale => sale.checked)

  const handleOnClick = (status) => {
    onCompleteSales && onCompleteSales(sales, status)
  }

  const handleChecked = (saleChecked, checked) => {
    console.log(sales[saleChecked])
    const ventas = sales[saleChecked].sales;

    checked ? setTotalSelected(totalSelected + ventas.reduce((acc, item) => acc + (item.prices_sold?.sale_price * item.quantity_sold), 0)) :
      setTotalSelected(totalSelected - ventas.reduce((acc, item) => acc + (item.prices_sold?.sale_price * item.quantity_sold), 0))
    sales[saleChecked].checked = checked
    // var aux = sales.map(sale => ({
    //   ...sale,
    //   //status: order._id === orderChecked._id ? checked ? STATUS_ORDER.CLOSED : STATUS_ORDER.OPENED : order.status
    //   checked: sale._id === saleChecked.id ? checked : sale.checked
    // }))
    // setSales(aux)
  }

  const handleDeleteItem = (sale, sales) => {
    onDeleteItem && onDeleteItem(sale, sales)
  }

  const handleReservedItem = (sale) => {
    onReserveItem && onReserveItem(sale, sales)
  }

  const handleReturnItem = (sale) => {
    onReturnItem && onReturnItem(sale)
  }

  const handleImprimir = () => {
    const contenido = refAImprimir.current.innerHTML;
    PrintService.print(contenido)
  }

  const isPendingGroup = (groupKey) => {
    const firstSale = sales[groupKey]?.sales?.[0]
    return firstSale?.status === STATUS_SALE.RESERVED
  }

  // Grupo web: al menos una venta tiene origin === 'transfer_order'
  // Estas órdenes tienen su propio flujo y no se pueden mover/entregar como venta común
  const isWebGroup = (groupKey) =>
    (sales[groupKey]?.sales || []).some((s) => s?.origin === 'transfer_order')

  const getGroupName = (groupKey) => sales[groupKey]?.sales?.[0]?.nameSale || ''
  const pendingGroupKeys = Object.keys(sales).filter((groupKey) => isPendingGroup(groupKey))
  const targetGroupKeys = mergeDialog.sourceKey
    ? pendingGroupKeys.filter((groupKey) => groupKey !== mergeDialog.sourceKey)
    : []

  const handleOpenMergeMenu = (event, sourceGroupKey) => {
    setMergeMenuAnchor(event.currentTarget)
    setMergeMenuSourceKey(sourceGroupKey)
  }

  const handleCloseMergeMenu = () => {
    setMergeMenuAnchor(null)
    setMergeMenuSourceKey('')
  }

  const handleStartMergeFromMenu = () => {
    if (!mergeMenuSourceKey) return
    setMergeDialog({ open: true, sourceKey: mergeMenuSourceKey, targetKey: '' })
    setSelectedMergeName('')
    handleCloseMergeMenu()
  }

  const handleCloseMergeDialog = () => {
    if (mergingSales) return
    setMergeDialog({ open: false, sourceKey: '', targetKey: '' })
    setSelectedMergeName('')
  }

  const handleOpenAddDepositFromMenu = () => {
    if (!mergeMenuSourceKey) return
    setDepositDialog({ open: true, sourceKey: mergeMenuSourceKey })
    setDepositAmount(0)
    handleCloseMergeMenu()
  }

  const handleAddItemsFromMenu = () => {
    if (!mergeMenuSourceKey || !onAddItems) return
    onAddItems(getGroupName(mergeMenuSourceKey), sales[mergeMenuSourceKey]?.sales || [])
    handleCloseMergeMenu()
  }

  const handleCloseDepositDialog = () => {
    if (addingDeposit) return
    setDepositDialog({ open: false, sourceKey: '' })
    setDepositAmount(0)
  }

  const handleConfirmDeposit = async () => {
    if (!depositDialog.sourceKey || !onAddDeposit || depositAmount <= 0) return
    const nameSale = getGroupName(depositDialog.sourceKey)
    try {
      setAddingDeposit(true)
      await onAddDeposit({ nameSale, amount: depositAmount })
      setDepositDialog({ open: false, sourceKey: '' })
      setDepositAmount(0)
    } finally {
      setAddingDeposit(false)
    }
  }

  const handleConfirmMerge = async () => {
    if (!mergeDialog.sourceKey || !mergeDialog.targetKey || !selectedMergeName || !onMergePendingSales) {
      handleCloseMergeDialog()
      return
    }

    try {
      setMergingSales(true)
      await onMergePendingSales({
        sourceSales: sales[mergeDialog.sourceKey]?.sales || [],
        targetSales: sales[mergeDialog.targetKey]?.sales || [],
        selectedName: selectedMergeName,
      })
      handleCloseMergeDialog()
    } finally {
      setMergingSales(false)
    }
  }

  const handleOpenDeliverDialog = (groupKey) => {
    const groupSales = sales[groupKey]?.sales || []
    setDeliverDialog({ open: true, sourceKey: groupKey })
    setSelectedDeliverIds(groupSales.map((item) => item._id))
    const heldMap = {}
    groupSales.forEach((s) => { heldMap[s._id] = false }) // default: se lleva, no queda en depósito (held=false)
    setDeliverHeldMap(heldMap)
  }

  const handleOpenDeliverFromMenu = () => {
    if (!mergeMenuSourceKey) return
    handleOpenDeliverDialog(mergeMenuSourceKey)
    handleCloseMergeMenu()
  }

  const handleOpenCancelGroupFromMenu = () => {
    if (!mergeMenuSourceKey) return
    setCancelGroupDialog({ open: true, sourceKey: mergeMenuSourceKey })
    handleCloseMergeMenu()
  }

  const handleOpenAssignRaffleFromMenu = async () => {
    if (!mergeMenuSourceKey) return
    const firstSale = sales[mergeMenuSourceKey]?.sales?.[0]
    const existingNumero = firstSale?.numero_rifa
    const existingLote = firstSale?.lote_rifa
    setRaffleForm({
      numero_rifa: existingNumero || '',
      lote_rifa: existingLote || '',
      dni: firstSale?.dni ?? '',
      telefono: firstSale?.telefono ?? '',
    })
    setRaffleError('')
    setRaffleDialog({ open: true, sourceKey: mergeMenuSourceKey })
    handleCloseMergeMenu()
    // Si no tiene número asignado, buscar el próximo disponible
    if (!existingNumero) {
      setRaffleLoadingNext(true)
      try {
        const next = await RaffleService.getNext(existingLote || undefined)
        setRaffleForm((prev) => ({ ...prev, numero_rifa: next.numero, lote_rifa: next.lote ?? prev.lote_rifa }))
      } catch (err) {
        console.error(err)
      } finally {
        setRaffleLoadingNext(false)
      }
    }
  }

  const handleCloseRaffleDialog = () => {
    if (assigningRaffle) return
    setRaffleDialog({ open: false, sourceKey: '' })
    setRaffleForm({ numero_rifa: '', lote_rifa: '', dni: '', telefono: '' })
    setRaffleError('')
    setRaffleLoadingNext(false)
  }

  const handleConfirmRaffleAssign = async () => {
    if (!raffleDialog.sourceKey || !onAssignRaffle) return
    const nameSale = sales[raffleDialog.sourceKey]?.sales?.[0]?.nameSale
    if (!nameSale) return
    try {
      setAssigningRaffle(true)
      setRaffleError('')
      await onAssignRaffle({
        nameSale: sales[raffleDialog.sourceKey]?.sales?.[0]?.nameSale,
        numero_rifa: Number(raffleForm.numero_rifa) || 0,
        lote_rifa: Number(raffleForm.lote_rifa) || 0,
        dni: raffleForm.dni || undefined,
        telefono: raffleForm.telefono || undefined,
      })
      handleCloseRaffleDialog()
    } catch (err) {
      const errData = err?.response?.data
      let msg
      if (typeof errData === 'string' && errData) msg = errData
      else if (errData?.message) msg = errData.message
      else if (errData?.error) msg = errData.error
      else if (errData?.detail) msg = errData.detail
      else msg = err?.message || 'Error al asignar rifa'
      setRaffleError(msg)
    } finally {
      setAssigningRaffle(false)
    }
  }

  const handleCloseCancelGroupDialog = () => {
    if (cancellingGroup) return
    setCancelGroupDialog({ open: false, sourceKey: '' })
  }

  const handleConfirmCancelGroup = async () => {
    if (!cancelGroupDialog.sourceKey || !onCancelGroup) return
    const groupSales = sales[cancelGroupDialog.sourceKey]?.sales || []
    if (!groupSales.length) return
    try {
      setCancellingGroup(true)
      await onCancelGroup(groupSales)
      handleCloseCancelGroupDialog()
    } finally {
      setCancellingGroup(false)
    }
  }

  const handleCloseDeliverDialog = () => {
    if (deliveringSales) return
    setDeliverDialog({ open: false, sourceKey: '' })
    setSelectedDeliverIds([])
    setDeliverHeldMap({})
  }

  const handleToggleDeliverSale = (saleId) => {
    setSelectedDeliverIds((prev) => prev.includes(saleId)
      ? prev.filter((id) => id !== saleId)
      : prev.concat(saleId))
  }

  const handleToggleDeliverHeld = (saleId) => {
    setDeliverHeldMap((prev) => ({ ...prev, [saleId]: !prev[saleId] }))
  }

  const handleConfirmDeliverGroup = async () => {
    if (!onDeliverGroup || !deliverDialog.sourceKey || selectedDeliverIds.length === 0) return
    const sourceSales = sales[deliverDialog.sourceKey]?.sales || []
    const selectedSales = sourceSales.filter((sale) => selectedDeliverIds.includes(sale._id))
    if (!selectedSales.length) return

    const heldUpdates = selectedSales
      .filter((sale) => Boolean(deliverHeldMap[sale._id]) !== Boolean(sale.held))
      .map((sale) => ({ idSale: sale._id, held: Boolean(deliverHeldMap[sale._id]) }))

    try {
      setDeliveringSales(true)
      await onDeliverGroup(selectedSales, heldUpdates)
      handleCloseDeliverDialog()
    } finally {
      setDeliveringSales(false)
    }
  }

  useEffect(() => {
    var saless = Object.keys(sales)?.map(sale => sales[sale].sales)?.map(
      s => ({
        name: s[0].nameSale,
        price: s.reduce((acc, item) => acc + (item.prices_sold?.sale_price * item.quantity_sold), 0)
      })
    )
    setToPrint(saless)
  }, [sales])

  useEffect(() => {
    const sourceSales = !(visualVariant === 'cashier' && onlyTodayPending)
      ? salesData
      : (salesData || []).filter((sale) => {
          if (!isReservedSale(sale)) return true
          return isSameLocalDate(sale?.created_at || sale?.created_sale || sale?.updated_at)
        })

    var groupSale = groupSales(sourceSales)
    if (['all', ''].includes(selectedUser)) {
      setSales(groupSale)
    } else {
      var saleFilter = Object.values(groupSale).filter(s => s.sales.some(sa => sa.userId === selectedUser))
      var saless = Object.values(saleFilter)?.map(
        s => ({
          name: s.sales[0].nameSale,
          price: s.sales.reduce((acc, item) => acc + (item.prices_sold?.sale_price * item.quantity_sold), 0)
        })
      )
      setToPrint(saless)
      setSales(saleFilter)
    }
  }, [selectedUser, salesData, onlyTodayPending, visualVariant])

  const depositInfo = depositDialog.open && depositDialog.sourceKey
    ? (() => {
        const gs = sales[depositDialog.sourceKey]?.sales || []
        const nameSale = gs[0]?.nameSale || ''
        const totalBruto = gs.reduce((s, x) => s + (x.quantity_sold || 0) * (x.prices_sold?.sale_price || 0), 0)
        const totalSeñado = gs.reduce((s, x) => s + (x.deposit || 0), 0)
        const remaining = Math.max(totalBruto - totalSeñado, 0)
        const progress = totalBruto > 0 ? Math.min((totalSeñado / totalBruto) * 100, 100) : 0
        return { nameSale, totalBruto, totalSeñado, remaining, progress }
      })()
    : null

  const getSaleAmounts = (sale) => {
    const gross = (sale.prices_sold?.sale_price || 0) * (sale.quantity_sold || 0)
    const deposit = Math.max(Number(sale.deposit || 0), 0)
    const pending = Math.max(gross - deposit, 0)
    return { gross, deposit, pending }
  }

  const deliverInfo = deliverDialog.open && deliverDialog.sourceKey
    ? (() => {
        const groupSales = sales[deliverDialog.sourceKey]?.sales || []
        const nameSale = groupSales[0]?.nameSale || ''
        const selectedSales = groupSales.filter((sale) => selectedDeliverIds.includes(sale._id))
        const totalItems = groupSales.length
        const selectedItems = selectedSales.length
        const selectedGrossAmount = selectedSales.reduce((sum, sale) => sum + getSaleAmounts(sale).gross, 0)
        const selectedDepositAmount = selectedSales.reduce((sum, sale) => sum + getSaleAmounts(sale).deposit, 0)
        const selectedPendingAmount = selectedSales.reduce((sum, sale) => sum + getSaleAmounts(sale).pending, 0)
        const progress = totalItems > 0 ? (selectedItems / totalItems) * 100 : 0
        return {
          nameSale,
          groupSales,
          totalItems,
          selectedItems,
          selectedGrossAmount,
          selectedDepositAmount,
          selectedPendingAmount,
          progress,
        }
      })()
    : null

  const formatSaleDate = (value) => {
    if (!value) return 'Sin fecha';
    const raw = String(value);
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      const [, year, month, day] = ymd;
      return `${day}/${month}/${year}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Sin fecha';
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (<>
    {tittle !== '' && <Typography variant="h6" color='navy'>{tittle}</Typography>}
    <List sx={{ overflowY: 'auto', width: '100%', padding: '0', margin: '0' }} >
      {
        <div ref={refAImprimir}>
          <ImprimirEtiquetas
            etiquetas={toPrint?.map(print => <Etiqueta producto={print.name} precio={print.price} />)}
          />
        </div>
      }
      <Box display='flex' flexDirection='column'>
        <RadioGroup options={userInSales} onValueChange={value => { console.log(value); setSelectedUser(value) }} />
        {visualVariant === 'cashier' && (
          <FormControlLabel
            sx={{ mt: 0.25, ml: 0.5 }}
            control={
              <Checkbox
                size='small'
                checked={onlyTodayPending}
                onChange={(event) => setOnlyTodayPending(event.target.checked)}
              />
            }
            label='Pendientes HOY'
          />
        )}
        {showImprimir && <Button style={{ alignSelf: 'end' }} onClick={handleImprimir}>Imprimir Etiquetas</Button>}
      </Box>
      {visualVariant === 'cashier'
        ? <CashierSaleGrid
            sales={sales}
            onOpenMenu={handleOpenMergeMenu}
            canMenu={(key) => (canMergeByAction || canAddDepositByAction || canDeliverByAction || canAssignRaffleByAction) && isPendingGroup(key) && !isWebGroup(key)}
            onDeleteItem={handleDeleteItem}
            canDeleteItem={(key) => Boolean(onDeleteItem) && !readonly && isPendingGroup(key) && !isWebGroup(key)}
            showPaidWatermark={showPaidWatermark}
            onDeliverHeld={onUpdateHeld
              ? async (saleList) => {
                  const updates = saleList.filter(s => s.held).map(s => ({ idSale: s._id, held: false }))
                  if (updates.length) await onUpdateHeld(updates)
                }
              : undefined
            }
          />
        : Object.keys(sales).map(sale => {
            const canOpenMergeMenu = (canMergeByAction || canAddDepositByAction || canDeliverByAction || canAssignRaffleByAction) && isPendingGroup(sale) && !isWebGroup(sale)
            return (<>
              <Box sx={{ borderRadius: 1, transition: 'all 0.15s ease' }}>
                <SaleData
                  asList={asList}
                  readonly={readonly}
                  tittle={sale}
                  sales={sales[sale]}
                  visualVariant={visualVariant}
                  onChecked={handleChecked}
                  onDeleteItem={handleDeleteItem}
                  onReserveItem={handleReservedItem}
                  onReturnItem={handleReturnItem}
                  groupByDate={groupByDate}
                  showPaidWatermark={showPaidWatermark && sales[sale]?.sales?.[0]?.status === STATUS_SALE.FINISHED}
                  mergeActionProps={canOpenMergeMenu ? {
                    onClick: (event) => handleOpenMergeMenu(event, sale),
                  } : undefined}
                />
              </Box>
              <Divider />
            </>)
          })
      }
    </List>
    <Dialog
      open={deliverDialog.open}
      onClose={handleCloseDeliverDialog}
      fullWidth
      maxWidth='sm'
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
          px: 2.5,
          pt: 2.2,
          pb: 1.8,
        }}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='subtitle1' fontWeight={800} color='white' letterSpacing={0.2}>
            Entregar pedidos del grupo
          </Typography>
          <IconButton
            size='small'
            onClick={handleCloseDeliverDialog}
            disabled={deliveringSales}
            sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: 'white' } }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Box display='flex' alignItems='center' gap={1} mt={1}>
          <Avatar sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.25)', fontSize: 12, fontWeight: 700 }}>
            {deliverInfo?.nameSale?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
          <Typography variant='body2' color='white' fontWeight={600}>
            {deliverInfo?.nameSale ?? ''}
          </Typography>
        </Box>
      </Box>

      <Box display='flex' sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box flex={1} py={1.2} textAlign='center' sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary'>Seleccionados</Typography>
          <Typography variant='h6' fontWeight={800} color='success.main' lineHeight={1.1}>
            {`${deliverInfo?.selectedItems ?? 0} / ${deliverInfo?.totalItems ?? 0}`}
          </Typography>
        </Box>
        <Box flex={1} py={1.2} textAlign='center'>
          <Typography variant='caption' color='text.secondary'>Monto a cobrar</Typography>
          <Typography variant='h4' fontWeight={900} color='text.primary' lineHeight={1} sx={{ mt: 0.25 }}>
            {`$ ${ccyFormat(deliverInfo?.selectedPendingAmount ?? 0)}`}
          </Typography>
          <Typography variant='caption' color='warning.main' fontWeight={700}>
            {`Seña: $ ${ccyFormat(deliverInfo?.selectedDepositAmount ?? 0)}`}
          </Typography>
        </Box>
      </Box>

      <Box px={2.5} pt={1.2} pb={0.4}>
        <Box display='flex' justifyContent='space-between' mb={0.6}>
          <Typography variant='caption' color='text.secondary'>Cobertura del grupo</Typography>
          <Typography variant='caption' fontWeight={700} color='success.main'>
            {`${Math.round(deliverInfo?.progress ?? 0)}%`}
          </Typography>
        </Box>
        <LinearProgress variant='determinate' value={deliverInfo?.progress ?? 0} color='success' sx={{ borderRadius: 4, height: 6 }} />
      </Box>

      <DialogContent sx={{ pt: 1.2 }}>
        <Box display='flex' justifyContent='flex-end' gap={1} mb={1.2}>
          <Button
            size='small'
            variant='text'
            onClick={() => setSelectedDeliverIds(deliverInfo?.groupSales?.map((sale) => sale._id) || [])}
            disabled={deliveringSales || !(deliverInfo?.groupSales?.length)}
          >
            Seleccionar todo
          </Button>
          <Button
            size='small'
            variant='text'
            color='inherit'
            onClick={() => setSelectedDeliverIds([])}
            disabled={deliveringSales || selectedDeliverIds.length === 0}
          >
            Limpiar
          </Button>
        </Box>

        {(deliverInfo?.groupSales || []).map((sale) => {
          const { gross, deposit, pending } = getSaleAmounts(sale)
          const checked = selectedDeliverIds.includes(sale._id)
          const soloAbona = Boolean(deliverHeldMap[sale._id])

          return (
            <Box
              key={sale._id}
              sx={{
                borderRadius: 1.4,
                border: '1px solid',
                borderColor: checked ? (soloAbona ? '#9fa8da' : 'success.light') : 'divider',
                backgroundColor: checked ? (soloAbona ? '#e8eaf6' : 'rgba(67,160,71,0.08)') : 'transparent',
                mb: 0.7,
                overflow: 'hidden',
              }}
            >
              <Box display='flex' alignItems='center' justifyContent='space-between' sx={{ py: 0.8, px: 0.6 }}>
                <Box display='flex' alignItems='center' gap={1}>
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggleDeliverSale(sale._id)}
                    size='small'
                  />
                  <Typography variant='body2' fontWeight={checked ? 700 : 500}>
                    {`${sale.quantity_sold} - ${sale.article?.name || 'Artículo'}`}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {`Fecha: ${formatSaleDate(sale.created_at || sale.created_sale || sale.updated_at)}`}
                  </Typography>
                </Box>
                <Box textAlign='right'>
                  <Typography variant='caption' color='text.secondary'>
                    {`Total: $ ${ccyFormat(gross)}`}
                  </Typography>
                  {deposit > 0 && (
                    <Typography variant='caption' sx={{ display: 'block', color: '#9A6700', fontWeight: 700 }}>
                      {`Seña: $ ${ccyFormat(deposit)}`}
                    </Typography>
                  )}
                  <Typography variant='caption' sx={{ display: 'block', color: '#2e7d32', fontWeight: 800 }}>
                    {`Debe: $ ${ccyFormat(pending)}`}
                  </Typography>
                </Box>
              </Box>
              {checked && (
                <Box
                  display='flex'
                  alignItems='center'
                  gap={0.75}
                  sx={{
                    px: 1.5, py: 0.5,
                    bgcolor: soloAbona ? '#c5cae9' : 'rgba(46,125,50,0.1)',
                    borderTop: '1px solid',
                    borderColor: soloAbona ? '#9fa8da' : 'success.light',
                  }}
                >
                  <Inventory2Outlined sx={{ fontSize: 13, color: soloAbona ? '#283593' : 'success.main' }} />
                  <Typography variant='caption' fontWeight={700} color={soloAbona ? '#283593' : 'success.main'} flex={1}>
                    {soloAbona ? 'Solo abona · queda en depósito' : 'Se lleva · sale del depósito'}
                  </Typography>
                  <Switch
                    size='small'
                    checked={!soloAbona}
                    onChange={() => handleToggleDeliverHeld(sale._id)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2e7d32' },
                    }}
                  />
                </Box>
              )}
            </Box>
          )
        })}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.2, gap: 1 }}>
        <Button onClick={handleCloseDeliverDialog} disabled={deliveringSales} variant='outlined' sx={{ flex: 1 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmDeliverGroup}
          disabled={deliveringSales || selectedDeliverIds.length === 0}
          variant='contained'
          color='success'
          sx={{ flex: 1 }}
        >
          {deliveringSales ? 'Entregando...' : 'Entregar seleccionados'}
        </Button>
      </DialogActions>
    </Dialog>
    <Menu
      anchorEl={mergeMenuAnchor}
      open={Boolean(mergeMenuAnchor)}
      onClose={handleCloseMergeMenu}
    >
      {canMergeByAction && (
        <MenuItem
          onClick={handleStartMergeFromMenu}
          disabled={!mergeMenuSourceKey || pendingGroupKeys.filter((groupKey) => groupKey !== mergeMenuSourceKey).length === 0}
        >
          <ListItemIcon><CallMerge fontSize='small' /></ListItemIcon>
          <ListItemText>Unir con...</ListItemText>
        </MenuItem>
      )}
      {canAddDepositByAction && (
        <MenuItem onClick={handleOpenAddDepositFromMenu}>
          <ListItemIcon><Savings fontSize='small' color='primary' /></ListItemIcon>
          <ListItemText>Agregar dinero</ListItemText>
        </MenuItem>
      )}
      {canAddItemsByAction && (
        <MenuItem onClick={handleAddItemsFromMenu}>
          <ListItemIcon><AddShoppingCartOutlined fontSize='small' color='primary' /></ListItemIcon>
          <ListItemText>Agregar artículos</ListItemText>
        </MenuItem>
      )}
      {canDeliverByAction && (
        <MenuItem onClick={handleOpenDeliverFromMenu}>
          <ListItemIcon><CheckCircleOutline fontSize='small' color='success' /></ListItemIcon>
          <ListItemText>Entregar pedidos...</ListItemText>
        </MenuItem>
      )}
      {canAssignRaffleByAction && (() => {
        const groupTotal = (sales[mergeMenuSourceKey]?.sales || []).reduce(
          (sum, s) => sum + (s.prices_sold?.sale_price || 0) * (s.quantity_sold || 0), 0
        )
        if (raffleMinimo > 0 && groupTotal < raffleMinimo) return null
        return (
          <MenuItem onClick={handleOpenAssignRaffleFromMenu}>
            <ListItemIcon><ConfirmationNumberOutlined fontSize='small' sx={{ color: '#7b4f9e' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: '#7b4f9e', fontWeight: 700 }}>Asignar rifa</ListItemText>
          </MenuItem>
        )
      })()}
      {canCancelGroupByAction && [
        <Divider key='div-cancel' />,
        <MenuItem key='cancel-group' onClick={handleOpenCancelGroupFromMenu}>
          <ListItemIcon><BlockOutlined fontSize='small' sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error.main', fontWeight: 700 }}>Cancelar pedido</ListItemText>
        </MenuItem>
      ]}

    </Menu>
    <Dialog
      open={depositDialog.open}
      onClose={handleCloseDepositDialog}
      fullWidth
      maxWidth='xs'
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Cabecera */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1565c0 0%, #0288d1 100%)',
          px: 2.5,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <Savings sx={{ color: 'white', fontSize: 22 }} />
            <Typography variant='subtitle1' fontWeight={700} color='white' letterSpacing={0.3}>
              Agregar pago parcial
            </Typography>
          </Box>
          <IconButton
            size='small'
            onClick={handleCloseDepositDialog}
            disabled={addingDeposit}
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Box display='flex' alignItems='center' gap={1} mt={1.5}>
          <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.25)', fontSize: 12, fontWeight: 700 }}>
            {depositInfo?.nameSale?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
          <Typography variant='body2' color='white' fontWeight={600}>
            {depositInfo?.nameSale ?? ''}
          </Typography>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Box display='flex' sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        {[
          { label: 'Total', value: depositInfo?.totalBruto ?? 0, color: 'text.primary' },
          { label: 'Señado', value: depositInfo?.totalSeñado ?? 0, color: 'success.main' },
          { label: 'Pendiente', value: depositInfo?.remaining ?? 0, color: 'warning.main' },
        ].map(({ label, value, color }, i, arr) => (
          <Box
            key={label}
            flex={1}
            display='flex'
            flexDirection='column'
            alignItems='center'
            py={1.5}
            sx={{ borderRight: i < arr.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Typography variant='caption' color='text.secondary' sx={{ mb: 0.3 }}>{label}</Typography>
            <Typography variant='body2' fontWeight={700} color={color}>
              ${ccyFormat(value)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Barra de progreso */}
      <Box px={2.5} pt={1.5} pb={0.5}>
        <Box display='flex' justifyContent='space-between' mb={0.5}>
          <Typography variant='caption' color='text.secondary'>Progreso de cobro</Typography>
          <Typography variant='caption' fontWeight={600} color='success.main'>
            {Math.round(depositInfo?.progress ?? 0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={depositInfo?.progress ?? 0}
          color='success'
          sx={{ borderRadius: 4, height: 6 }}
        />
      </Box>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {addingDeposit ? (
          <Box display='flex' justifyContent='center' alignItems='center' minHeight={80}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <TextField
            autoFocus
            label='Monto a agregar'
            type='number'
            value={depositAmount === 0 ? '' : depositAmount}
            onChange={(e) => setDepositAmount(Math.max(0, Number(e.target.value) || 0))}
            error={depositAmount > (depositInfo?.remaining ?? 0)}
            helperText={
              depositAmount > (depositInfo?.remaining ?? 0)
                ? `No puede superar $${ccyFormat(depositInfo?.remaining ?? 0)}`
                : 'Enter confirma · Esc cancela'
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirmDeposit() }
              if (e.key === 'Escape') { e.preventDefault(); handleCloseDepositDialog() }
            }}
            InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
            fullWidth
            inputProps={{ min: 0 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleCloseDepositDialog}
          variant='outlined'
          disabled={addingDeposit}
          sx={{ flex: 1 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmDeposit}
          variant='contained'
          disabled={addingDeposit || depositAmount <= 0 || depositAmount > (depositInfo?.remaining ?? 0)}
          sx={{ flex: 1 }}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={mergeDialog.open} onClose={handleCloseMergeDialog} fullWidth maxWidth='xs'>
      <DialogTitle>Unir ventas pendientes</DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ mb: 0.6 }}>
          Elegí la otra venta pendiente para unir.
        </Typography>
        <MuiRadioGroup
          value={mergeDialog.targetKey}
          onChange={(event) => {
            const targetKey = event.target.value
            setMergeDialog((prev) => ({ ...prev, targetKey }))
            setSelectedMergeName(getGroupName(targetKey))
          }}
        >
          {targetGroupKeys.map((targetKey) => (
            <FormControlLabel
              key={targetKey}
              value={targetKey}
              control={<Radio />}
              label={getGroupName(targetKey)}
            />
          ))}
        </MuiRadioGroup>
        {!targetGroupKeys.length && (
          <Typography variant='caption' color='text.secondary'>
            No hay otra venta pendiente disponible para unir.
          </Typography>
        )}

        {mergeDialog.targetKey && (
          <>
            <Typography variant='body2' sx={{ mb: 1.5, mt: 1.5 }}>
              Elegí cuál nombre querés conservar para la unión.
            </Typography>
            <MuiRadioGroup
              value={selectedMergeName}
              onChange={(event) => setSelectedMergeName(event.target.value)}
            >
              <FormControlLabel value={getGroupName(mergeDialog.sourceKey)} control={<Radio />} label={getGroupName(mergeDialog.sourceKey)} />
              <FormControlLabel value={getGroupName(mergeDialog.targetKey)} control={<Radio />} label={getGroupName(mergeDialog.targetKey)} />
            </MuiRadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseMergeDialog} disabled={mergingSales}>Cancelar</Button>
        <Button onClick={handleConfirmMerge} disabled={mergingSales || !mergeDialog.targetKey || !selectedMergeName} variant='contained'>
          {mergingSales ? 'Uniendo...' : 'Unir'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Asignar rifa Dialog ────────────── */}
    <Dialog
      open={raffleDialog.open}
      onClose={handleCloseRaffleDialog}
      maxWidth='xs'
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ background: 'linear-gradient(135deg, #7b4f9e 0%, #c0714a 100%)', px: 2.5, pt: 2.2, pb: 1.8 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <ConfirmationNumberOutlined sx={{ color: 'white', fontSize: 22 }} />
            <Typography variant='subtitle1' fontWeight={700} color='white' letterSpacing={0.2}>Asignar rifa</Typography>
          </Box>
          <IconButton size='small' onClick={handleCloseRaffleDialog} disabled={assigningRaffle}
            sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Box display='flex' alignItems='center' gap={1} mt={1}>
          <Avatar sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.25)', fontSize: 12, fontWeight: 700 }}>
            {sales[raffleDialog.sourceKey]?.sales?.[0]?.nameSale?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
          <Typography variant='body2' color='white' fontWeight={600}>
            {sales[raffleDialog.sourceKey]?.sales?.[0]?.nameSale ?? ''}
          </Typography>
        </Box>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        <Box display='flex' gap={1.5} mb={1.5}>
          <TextField
            label='Nro. rifa'
            type='number'
            size='small'
            value={raffleForm.numero_rifa}
            inputProps={{ min: 0, readOnly: true }}
            helperText={raffleLoadingNext ? 'Buscando próximo disponible…' : undefined}
            disabled={raffleLoadingNext}
            InputProps={raffleLoadingNext ? { endAdornment: <CircularProgress size={14} sx={{ color: '#7b4f9e' }} /> } : undefined}
            sx={{ flex: 1 }}
          />
          <TextField
            label='Lote'
            type='number'
            size='small'
            value={raffleForm.lote_rifa}
            inputProps={{ min: 1, readOnly: true }}
            sx={{ flex: 1 }}
          />
        </Box>
        <Box display='flex' flexDirection='column' gap={1.5}>
          <TextField
            label='DNI'
            size='small'
            value={raffleForm.dni}
            onChange={(e) => setRaffleForm((p) => ({ ...p, dni: e.target.value }))}
            fullWidth
          />
          <TextField
            label='Teléfono'
            size='small'
            value={raffleForm.telefono}
            onChange={(e) => setRaffleForm((p) => ({ ...p, telefono: e.target.value }))}
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); handleConfirmRaffleAssign() }
              if (e.key === 'Escape') { e.preventDefault(); handleCloseRaffleDialog() }
            }}
          />
        </Box>
        {raffleError && (
          <Box mt={1.5} p={1.25} bgcolor='#FFEBEE' borderRadius={1.5} border='1px solid #ef9a9a'>
            <Typography variant='caption' color='error' fontWeight={600}>{raffleError}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={handleCloseRaffleDialog} variant='outlined' disabled={assigningRaffle} sx={{ flex: 1 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmRaffleAssign}
          variant='contained'
          disabled={assigningRaffle || raffleLoadingNext || !raffleForm.numero_rifa}
          sx={{ flex: 1, background: 'linear-gradient(135deg, #7b4f9e 0%, #c0714a 100%)', '&:hover': { filter: 'brightness(0.9)' } }}
        >
          {assigningRaffle ? <CircularProgress size={18} color='inherit' /> : 'Asignar'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── Cancelar grupo Dialog ────────────── */}
    <Dialog
      open={cancelGroupDialog.open}
      onClose={handleCloseCancelGroupDialog}
      maxWidth='xs'
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ background: 'linear-gradient(135deg,#c62828 0%,#e53935 100%)', px: 2.5, pt: 2.5, pb: 2 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <BlockOutlined sx={{ color: 'white', fontSize: 22 }} />
            <Typography variant='subtitle1' fontWeight={700} color='white'>Cancelar pedido</Typography>
          </Box>
          <IconButton size='small' onClick={handleCloseCancelGroupDialog} disabled={cancellingGroup}
            sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
        <Typography variant='body2' color='rgba(255,255,255,0.85)' mt={1.2}>
          {`Se cancelarán todas las ventas del pedido "${sales[cancelGroupDialog.sourceKey]?.sales?.[0]?.nameSale || ''}"`}
        </Typography>
      </Box>
      <DialogContent>
        <Box display='flex' alignItems='center' gap={1} p={1} bgcolor='#fff3e0' borderRadius={1.5} border='1px solid #ffe0b2'>
          <WarningAmberRounded sx={{ color: '#e65100', fontSize: 20 }} />
          <Typography variant='caption' color='#e65100' fontWeight={600}>
            {`Esta acción cancelará ${sales[cancelGroupDialog.sourceKey]?.sales?.length || 0} venta(s). No se puede deshacer.`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={handleCloseCancelGroupDialog} variant='outlined' disabled={cancellingGroup} sx={{ flex: 1 }}>
          Volver
        </Button>
        <Button onClick={handleConfirmCancelGroup} variant='contained' color='error' disabled={cancellingGroup} sx={{ flex: 1 }}>
          {cancellingGroup ? 'Cancelando...' : 'Confirmar cancelación'}
        </Button>
      </DialogActions>
    </Dialog>

    <Box display='flex' flexDirection='column' paddingTop={2}>
      <Box display='flex' justifyContent='space-between'>
        {!readonly && showButton && <Button color='success' sx={{ alignSelf: 'flex-end' }} onClick={() => handleOnClick(STATUS_SALE.FINISHED)}>Entregar ventas</Button>}
        {!readonly && showButton && <Button color='error' sx={{ alignSelf: 'flex-end' }} onClick={() => handleOnClick(STATUS_SALE.CANCELED)}>Cancelar ventas</Button>}
      </Box>
      {!readonly && <Box display='flex' flexDirection='row' justifyContent='space-between'>
      </Box>}
      {!readonly && <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <Typography variant="h6" aria-label="delete">
          Total seleccionado
        </Typography>
        <Typography variant="h6" aria-label="delete">
          {`$ ${ccyFormat(totalSelected)}`}
        </Typography>
      </Box>}
      <Divider />
      {showTotals && <>
        <Box display='flex' flexDirection='row' justifyContent='space-between'>
          <Typography variant="caption" aria-label="delete">
            Efectivo
          </Typography>
          <Typography variant="caption" aria-label="delete">
            {`$ ${ccyFormat(totalQuant2)}`}
          </Typography>
        </Box>
        <Box display='flex' flexDirection='row' justifyContent='space-between'>
          <Typography variant="caption" aria-label="delete">
            Transferencia
          </Typography>
          <Typography variant="caption" aria-label="delete">
            {`$ ${ccyFormat(totalQuant3)}`}
          </Typography>
        </Box>
      </>}
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <Typography variant="h6" aria-label="delete">
          {`${totalQuant} - TOTAL `}
        </Typography>
        <Typography variant="h6" aria-label="delete">
          {`$ ${ccyFormat(totalSum2)}`}
        </Typography>
      </Box>
    </Box>
  </>)
}

export default Sales;